/**
 * PooCalendar — role-neutral booking calendar (Backlog A).
 *
 * Wraps `react-big-calendar` with a brand-tinted shell + a pluggable
 * `eventStyleResolver` so the SAME component serves both Provider
 * scope (`/dashboard/provider/schedule`, today) and Admin scope
 * (`/admin/bookings`, Backlog E). Provider scope uses a strict subset
 * of the API; admin scope opts into color-by-provider, filters,
 * legend, and dense density without forcing breaking changes.
 *
 * Recurrence rendering: materialized-only. We render the rows the
 * backend has persisted in `bookings`; we do NOT virtualize future
 * occurrences. The backend materializes the next occurrence at
 * complete-time (see `bookings.py::_generate_next_occurrence`), so
 * the calendar always reflects current truth without an RRULE engine
 * in the client.
 *
 * Mobile pattern: this component is a PURE render. It does NOT detect
 * viewport. Consumers (e.g. ProviderSchedulePage) override `mode="day"`
 * below `md:` themselves via `matchMedia('(max-width: 767px)')` and
 * hide their own mode-toggle chrome. Keeping viewport detection out
 * of the calendar means admin (which never lands on phone-sized
 * surfaces in v1) doesn't inherit any mobile-specific hacks.
 *
 * A11y notes (Backlog A v1 — D1):
 *   - Root carries `role="region"` + `aria-label` for landmark nav.
 *   - Each event pill carries an `aria-label` describing status +
 *     window + customer (via `formatStatusForScreenReader`).
 *   - The `.pp-live-dot` carries `aria-hidden="true"` (decorative).
 *   - Arrow-key inter-cell nav is NOT shipped by react-big-calendar
 *     and NOT polyfilled here — logged to Backlog A-FUTURE.
 *
 * Admin-scope API surface (forward-looking — provider v1 doesn't use):
 *   - `eventStyleResolver` returns `{statusClass, accentColor, chipLabel}`
 *   - `filters`, `onFilterChange`, `providerLegend`, `showFilterBar`
 *     for filter chrome
 *   - `density="compact"` / `"dense"` for tighter cells
 *   - `toolbarSlot` ReactNode for filter/legend chrome alongside
 *     the RBC built-in toolbar
 *
 * Backlog E.4 prop addition:
 *   - `showCancelled` (default `false`) — when true, cancelled
 *     bookings stay in the rendered grid. Default `false` preserves
 *     the pre-E.4 behavior for `ProviderSchedulePage` (and any future
 *     non-admin consumers). Admin scope opts in via
 *     `<AdminCalendarView>`, which passes `showCancelled={true}` and
 *     supplies a resolver that mutes cancelled events (grey accent
 *     + strikethrough class) so completed vs cancelled stay visually
 *     distinct.
 */
import React, { useMemo, useState } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import enUS from "date-fns/locale/en-US";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./poo-calendar.css";
import { cn } from "@/lib/utils";
import { defaultProviderResolver } from "./useEventStyleResolver";

const locales = { "en-US": enUS };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 0 }),
  getDay,
  locales,
});

/**
 * Parse an ISO `yyyy-mm-dd` plus `HH:mm` into a local Date — never UTC.
 * Using `new Date("2026-06-24")` would shift the day in non-UTC timezones,
 * so we build the Date from explicit components.
 */
function isoToLocalDate(isoDate, hhmm) {
  const [y, m, d] = isoDate.split("-").map((n) => parseInt(n, 10));
  const [hh, mm] = (hhmm || "00:00").split(":").map((n) => parseInt(n, 10));
  return new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0);
}

/**
 * Map an internal status enum to a screen-reader-friendly phrase. D1
 * a11y fix — `in_progress` reads as "in progress" not "in underscore
 * progress". Kept local; not exported because no other consumer needs
 * this exact phrasing.
 */
function formatStatusForScreenReader(status) {
  const map = {
    payment_pending: "payment pending",
    accepted: "accepted",
    in_progress: "in progress",
    completed: "completed",
    cancelled: "cancelled",
  };
  return map[status] || "booking";
}

/**
 * Event pill — visual content of one booking inside the grid.
 *
 * Carries an `aria-label` so screen readers get the status + time
 * window + customer name without having to puzzle out "9:00–13:00 ·
 * Connor T." in isolation.
 */
function EventPill({ event }) {
  const b = event.resource;
  const live = b?.status === "in_progress";
  const winStart = b?.preferred_window?.start || "";
  const winEnd = b?.preferred_window?.end || "";
  const customer = b?.customer_name || "Customer";
  const ariaLabel = `${formatStatusForScreenReader(b?.status)} booking, ${winStart} to ${winEnd}, ${customer}`;
  return (
    <div
      className="flex items-center gap-1 text-xs leading-tight"
      aria-label={ariaLabel}
    >
      {live && <span className="pp-live-dot" aria-hidden="true" />}
      <span className="truncate">
        {winStart}–{winEnd} · {customer}
      </span>
    </div>
  );
}

/**
 * PooCalendar — props per Step 0 §4 / Step 1 §1.
 *
 * Provider v1 uses: events, mode, views, eventStyleResolver,
 * onSelectEvent, onSelectSlot, testidPrefix, height. The rest are
 * forward-looking for the admin scope landing in Backlog E.
 */
export default function PooCalendar({
  // Data
  events: bookings,

  // View mode
  views = ["month", "week"],
  mode = "month",
  onModeChange,
  date: controlledDate,
  onNavigate,

  // Event styling (scope-aware)
  eventStyleResolver = defaultProviderResolver,
  renderEvent,

  // Interactions
  onSelectEvent,
  onSelectSlot,

  // Layout / identity
  height = 640,
  toolbarSlot,
  ariaLabel = "Booking calendar",
  testidPrefix = "pp-calendar",
  className,
  showCancelled = false,
}) {
  // Uncontrolled date cursor (RBC needs a `date` to navigate forward/back).
  const [internalDate, setInternalDate] = useState(() => new Date());
  const date = controlledDate ?? internalDate;
  const handleNavigate = (d) => {
    if (onNavigate) onNavigate(d);
    else setInternalDate(d);
  };

  // Build RBC events. Cancelled bookings are hidden by default —
  // admin scope opts in via `showCancelled={true}` (see JSDoc).
  const events = useMemo(() => {
    const visible = (bookings || []).filter(
      (b) => showCancelled || b.status !== "cancelled"
    );
    return visible.map((b) => {
      const start = isoToLocalDate(b.service_date, b.preferred_window?.start);
      const end = isoToLocalDate(b.service_date, b.preferred_window?.end);
      const title = `${b.preferred_window?.start || ""}–${b.preferred_window?.end || ""} · ${b.customer_name || "Customer"}`;
      return {
        id: b.id,
        title,
        start,
        end,
        allDay: false,
        resource: b,
      };
    });
  }, [bookings, showCancelled]);

  const eventPropGetter = (event) => {
    const style = eventStyleResolver(event.resource) || {};
    const classes = ["rbc-evt", style.statusClass || "", style.extraClass || ""].filter(Boolean);
    return {
      className: cn(...classes),
      // Admin scope: accentColor overrides the brand fill so each
      // provider's events render in a distinct hash-derived hue.
      // Provider scope leaves accentColor undefined → no inline style.
      ...(style.accentColor
        ? { style: { background: style.accentColor, color: "#fff", borderColor: style.accentColor } }
        : {}),
    };
  };

  return (
    <div
      className={cn("poo-calendar", className)}
      role="region"
      aria-label={ariaLabel}
      data-testid={testidPrefix}
    >
      {toolbarSlot ? (
        <div className="mb-2 flex justify-end" data-testid={`${testidPrefix}-toolbar-slot`}>
          {toolbarSlot}
        </div>
      ) : null}
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        view={mode}
        // The page-level mode toggle is the canonical control. RBC's
        // built-in toolbar still renders Back / Today / Next, but its
        // view-switcher buttons are wired through onModeChange so an
        // admin caller that doesn't render its own toggle can still
        // switch views. Provider scope passes no `onModeChange` →
        // RBC's view buttons become inert (matches legacy behavior).
        onView={(v) => onModeChange && onModeChange(v)}
        date={date}
        onNavigate={handleNavigate}
        views={views}
        toolbar
        popup
        selectable
        eventPropGetter={eventPropGetter}
        onSelectEvent={(e) => onSelectEvent && onSelectEvent(e.resource)}
        onSelectSlot={(s) => {
          if (!onSelectSlot) return;
          const d = s.start;
          const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
          onSelectSlot(iso);
        }}
        style={{ height }}
        components={renderEvent
          ? { event: (props) => renderEvent(props.event.resource) }
          : { event: EventPill }}
      />
    </div>
  );
}
